import {
  Box,
  Image,
  List,
  ListItem,
  ListProps,
  Tooltip,
} from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";

const availableBrokerFragment = gql(/* GraphQL */ `
  fragment AvailableBrokerForIcons on AvailableBroker {
    broker {
      id
      name
      processedIcon {
        id
        width
        height
        fileUrl
      }
    }
  }
`);

const brandRetailerFragment = gql(/* GraphQL */ `
  fragment BrandRetailerBrokerIcons on BrandRetailer {
    availableBrokersList {
      ...AvailableBrokerForIcons
    }
  }
`);

const retailerFragment = gql(/* GraphQL */ `
  fragment RetailerBrokerIcons on Retailer {
    availableBrokersForBrandList(brandId: $brandId) {
      ...AvailableBrokerForIcons
    }
  }
`);

type AvailableBrokerForIconsFragment = FragmentType<
  typeof availableBrokerFragment
>;

type BrandRetailerBrokerIconsFragment = FragmentType<
  typeof brandRetailerFragment
>;

export type RetailerBrokerIconsFragment = FragmentType<typeof retailerFragment>;

type BrokerIconsListProps = ListProps & {
  availableBrokers: AvailableBrokerForIconsFragment[] | null | undefined;
};

export function BrokerIconsList({
  availableBrokers: availableBrokersFragment,
  ...rest
}: BrokerIconsListProps) {
  const availableBrokers = getFragmentData(
    availableBrokerFragment,
    availableBrokersFragment,
  );
  if (!availableBrokers?.length) return null;
  return (
    <List display="inline-flex" alignItems="baseline" gap={1} {...rest}>
      {availableBrokers.map((availableBroker) => {
        const { broker } = availableBroker;

        return (
          <Tooltip
            key={broker.id}
            label={`This retailer is a ${broker.name} account`}
            hasArrow
            whiteSpace="nowrap"
            maxW="50vw"
          >
            <ListItem
              display="flex"
              alignItems="center"
              justifyContent="center"
              flex="0 0 1em"
            >
              {broker.processedIcon?.fileUrl ? (
                <Image
                  src={broker.processedIcon.fileUrl}
                  alt={broker.name}
                  htmlWidth={broker.processedIcon.width}
                  htmlHeight={broker.processedIcon.height}
                  boxSize="1em"
                  objectFit="contain"
                  objectPosition="center center"
                />
              ) : (
                <Box
                  as="span"
                  display="block"
                  boxSize="1em"
                  position="relative"
                  bg="gray.300"
                  borderRadius="full"
                  color="gray.600"
                  fontWeight="bold"
                  verticalAlign="middle"
                  _before={{
                    content: `"${broker.name.charAt(0).toUpperCase()}"`,
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "0.65em",
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        );
      })}
    </List>
  );
}

type RetailerProps = ListProps & {
  retailer: RetailerBrokerIconsFragment;
};

export default function RetailerBrokerIcons({
  retailer: retailerFragmentData,
  ...rest
}: RetailerProps) {
  const data = getFragmentData(retailerFragment, retailerFragmentData);
  return (
    <BrokerIconsList
      availableBrokers={data.availableBrokersForBrandList}
      {...rest}
    />
  );
}

type BrandRetailerProps = ListProps & {
  brandRetailer: BrandRetailerBrokerIconsFragment;
};

export function BrandRetailerBrokerIcons({
  brandRetailer: brandRetailerFragmentData,
  ...rest
}: BrandRetailerProps) {
  const data = getFragmentData(
    brandRetailerFragment,
    brandRetailerFragmentData,
  );
  return (
    <BrokerIconsList availableBrokers={data.availableBrokersList} {...rest} />
  );
}
